import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import NotificationBell from "../components/NotificationBell";
import { EmailSettingsButton } from "../components/EmailSettingsButton";
import {
  Plus, Inbox, CreditCard, BarChart3, Store, Calendar, X, ChevronDown,
  TrendingUp, TrendingDown, ArrowUpRight, Clock, Wallet, Receipt, Flame,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SITE_PRESETS = [
  { key: "today",     label: "Today" },
  { key: "last7",     label: "Last 7 Days" },
  { key: "thismonth", label: "This Month" },
];

const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const presetRange = (key) => {
  const now = new Date();
  if (key === "today") return { from: fmtYMD(now), to: fmtYMD(now) };
  if (key === "last7") { const s = new Date(now); s.setDate(now.getDate() - 6); return { from: fmtYMD(s), to: fmtYMD(now) }; }
  if (key === "thismonth") return { from: fmtYMD(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmtYMD(now) };
  return {};
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [spark, setSpark] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");
  const [sites, setSites] = useState([]);
  const [chartPreset, setChartPreset] = useState("thismonth");
  const [chartData, setChartData] = useState(null);  // dashboard scoped to chart preset for the bar chart
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async (from, to, site) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (site) params.site = site;
      const r = await api.get("/dashboard", { params });
      setData(r.data);
    } finally { setLoading(false); }
  }, []);

  const loadChart = useCallback(async (presetKey, site) => {
    const { from, to } = presetRange(presetKey);
    const params = { from, to };
    if (site) params.site = site;
    try {
      const r = await api.get("/dashboard", { params });
      setChartData(r.data);
    } catch (e) { /* noop */ }
  }, []);

  const loadSpark = useCallback(async (site) => {
    try {
      const r = await api.get("/dashboard/sparkline", { params: { days: 7, ...(site ? { site } : {}) } });
      setSpark(r.data);
    } catch (e) { setSpark(null); }
  }, []);

  useEffect(() => { api.get("/sites").then((r) => setSites(r.data?.sites || [])).catch(() => {}); }, []);
  useEffect(() => {
    // v2.13 — requestors only need the minimal /dashboard payload; skip
    // sparkline + chart (both are role-forbidden).
    load();
    if (user?.role !== "requestor") {
      loadSpark();
      loadChart(chartPreset);
    }
    /* eslint-disable-next-line */
  }, []);

  const openDrilldown = useCallback(async (site) => {
    setDrilldown({ site, data: null });
    setDrilldownLoading(true);
    try {
      const periodMap = { today: "eod", last7: "eow", thismonth: "eom" };
      const r = await api.get("/reports/site-drilldown", { params: { site, period: periodMap[chartPreset] || "eom" } });
      setDrilldown({ site, data: r.data });
    } catch (e) { setDrilldown(null); }
    finally { setDrilldownLoading(false); }
  }, [chartPreset]);

  const applyFilter = () => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    load(fromDate, toDate, siteFilter);
    setShowFilter(false);
  };
  const clearFilter = () => { setFromDate(""); setToDate(""); setShowFilter(false); load(undefined, undefined, siteFilter); };
  const changeSite = (s) => {
    setSiteFilter(s);
    load(fromDate || undefined, toDate || undefined, s || undefined);
    loadChart(chartPreset, s || undefined);
    loadSpark(s || undefined);
  };
  const changeChartPreset = (k) => { setChartPreset(k); loadChart(k, siteFilter || undefined); };

  const role = user?.role;
  // v2.11 — permission-driven gates. Prefer these over role-string checks.
  const perms = user?.permissions_effective || {};
  const isAdmin = role === "admin";
  // v2.13 (rev) — Recent Activity is restored ONLY for the Master Admin. Every
  // other role (including other admins) sees a cleaner dashboard without it.
  const isMasterAdmin = user?.is_master_admin === true;
  const canCreateRequest    = isAdmin || !!perms.create_requests;
  const canApproveRequests  = isAdmin || !!perms.approve_requests;
  const canViewQueue        = isAdmin || !!perms.view_payment_queue;
  const canViewReports      = isAdmin || !!perms.view_reports;
  const isFiltered = !!(data && (data.from || data.to));
  const recent = isMasterAdmin ? (data?.recent_approvals || []) : [];
  const todayLabel = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const firstName = (user?.name || user?.email || "").split(/\s|@/)[0] || "there";

  const sparkPoints = spark?.points || [];

  if (loading && !data) {
    return (
      <PageShell sidebar>
        <div className="px-8 py-10 font-mono text-sm text-slate-500">Loading dashboard…</div>
      </PageShell>
    );
  }

  // v2.13 — Requestors get a strictly-scoped "My Requests" summary. No site-wide
  // totals, spend charts, approval feed, or activity of others. Their entire
  // dashboard is their own request status counts + a shortcut to raise a new one.
  if (role === "requestor") {
    return (
      <PageShell sidebar>
        <RequestorDashboard user={user} data={data} loading={loading} navigate={navigate} />
      </PageShell>
    );
  }

  return (
    <PageShell sidebar>
      <div className="px-5 sm:px-8 lg:px-10 xl:px-14 py-6 lg:py-10 max-w-[1600px] mx-auto pb-32 lg:pb-10">

        {/* ───── Header row: welcome + filters ───── */}
        <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 lg:gap-8 mb-7 lg:mb-10">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-[44px] leading-none tracking-tight text-slate-900">
              Welcome back, <span className="text-olive">{firstName}</span>
            </h1>
            <p className="mt-3 font-mono text-sm text-slate-500">Here's what's happening across your sites today.</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-400">{todayLabel}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {(user?.all_sites || (user?.assigned_sites || []).length > 1) && (
              <PillDropdown
                testid="dash-site-filter"
                value={siteFilter}
                onChange={changeSite}
                placeholder="All Sites"
                options={[
                  ...(user?.all_sites ? [{ value: "", label: "All Sites" }] : []),
                  ...sites.map((s) => ({ value: s, label: s })),
                ]}
              />
            )}
            <PillButton testid="open-date-filter" onClick={() => setShowFilter((s) => !s)} icon={Calendar}>
              {isFiltered ? `${data.from} → ${data.to}` : "This Month"}
            </PillButton>
            {isFiltered && (
              <button data-testid="clear-date-filter" onClick={clearFilter} className="font-mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-olive flex items-center gap-1">
                <X size={11}/> Clear
              </button>
            )}
            <span data-testid="header-role-pill" className="hidden lg:inline-flex font-mono text-[10px] uppercase tracking-widest bg-olive/10 text-olive border border-olive/30 px-2.5 py-1.5 rounded-full font-bold">
              {role}
            </span>
            <div className="hidden lg:flex items-center gap-2">
              <NotificationBell />
              <EmailSettingsButton />
            </div>
          </div>
        </header>

        {/* Date-range collapsible */}
        {showFilter && (
          <div data-testid="date-filter-panel" className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/80 p-5 mb-7 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <LabeledInput label="From" testid="filter-from" type="date" value={fromDate} onChange={setFromDate} />
            <LabeledInput label="To" testid="filter-to" type="date" value={toDate} onChange={setToDate} />
            <button
              data-testid="apply-date-filter"
              onClick={applyFilter}
              disabled={!fromDate || !toDate}
              className="bg-olive text-white px-5 py-3 rounded-xl font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive-700 active:scale-[0.98] transition disabled:opacity-40"
            >Apply Range</button>
          </div>
        )}

        {/* ───── Top summary cards (3) ───── */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-7 lg:mb-10">
          <SummaryCard
            testid="card-total-spend"
            title="Total Spend"
            primaryValue={formatINR(data.total_spend_today)}
            primaryLabel={isFiltered ? "In Range" : "Today"}
            secondaryValue={spark ? formatINR(spark.mtd_spend) : "—"}
            secondaryLabel="This Month"
            accent="olive"
            delta={spark?.mom_delta_pct}
            spark={sparkPoints.map((p) => p.spend)}
          />
          <SummaryCard
            testid="card-pending"
            title="Pending Approvals"
            primaryValue={data.pending_count}
            primaryLabel={`${data.pending_count === 1 ? "Request" : "Requests"}`}
            secondaryValue={formatINR(data.pending_amount)}
            secondaryLabel="Pending Value"
            accent="amber"
            spark={sparkPoints.map((p) => p.pending)}
            onClick={() => canViewQueue && navigate("/approvals")}
          />
          <SummaryCard
            testid="card-payments-due"
            title="Payments Due"
            primaryValue={data.payments_due_count}
            primaryLabel={`${data.payments_due_count === 1 ? "Payment" : "Payments"}`}
            secondaryValue={formatINR(data.payments_due_amount)}
            secondaryLabel="Total Outstanding"
            accent="red"
            spark={sparkPoints.map((p) => p.payments_due)}
            onClick={() => canViewQueue && navigate("/queue")}
          />
        </section>

        {/* ───── Main grid: Site Spend (full width for admins/roles; 2/3 with Recent Activity for Master Admin) ───── */}
        <section className={`grid grid-cols-1 gap-4 lg:gap-6 ${isMasterAdmin ? "lg:grid-cols-3" : ""}`}>
          {/* Site Spend */}
          <div className={`bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/80 p-5 lg:p-7 ${isMasterAdmin ? "lg:col-span-2" : ""}`}>
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div>
                <h2 className="font-display font-semibold text-lg lg:text-xl text-slate-900 tracking-tight">Site Spend</h2>
                <p className="text-xs text-slate-500 mt-1">
                  {SITE_PRESETS.find((p) => p.key === chartPreset)?.label || "—"} · tap a bar to drill down
                </p>
              </div>
              <div className="flex items-center gap-1 p-1 bg-slate-100/80 rounded-full" data-testid="chart-preset-tabs">
                {SITE_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    data-testid={`chart-preset-${p.key}`}
                    onClick={() => changeChartPreset(p.key)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
                      chartPreset === p.key ? "bg-olive text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >{p.label}</button>
                ))}
              </div>
            </div>
            <SiteSpendChart
              sites={(chartData?.top_sites_today && chartData.top_sites_today.length > 0)
                ? chartData.top_sites_today
                : (data.top_sites_today || [])}
              onSelect={openDrilldown}
            />
          </div>

          {/* Recent Activity — Master Admin only (restored v2.13.1) */}
          {isMasterAdmin && (
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/80 p-5 lg:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg text-slate-900">Recent Activity</h2>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Last {recent.length || "—"}</span>
              </div>
              <div className="space-y-3" data-testid="recent-activity-list">
                {recent.length === 0 ? (
                  <div className="py-10 text-center font-mono text-xs text-slate-400 uppercase tracking-wider">No activity yet</div>
                ) : (
                  recent.slice(0, 6).map((r) => <ActivityRow key={r.request_id} r={r} onClick={() => navigate(`/requests/${r.request_id}`)} />)
                )}
              </div>
              {recent.length > 0 && (
                <button onClick={() => navigate("/my-requests")} className="mt-4 font-mono text-xs font-bold text-olive hover:text-olive-700 inline-flex items-center gap-1">
                  View all <ArrowUpRight size={13}/>
                </button>
              )}
            </div>
          )}
        </section>

        {/* ───── Quick Actions ───── */}
        <section className="mt-7 lg:mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {canCreateRequest && (
              <QuickActionCard testid="qa-new" icon={Plus} label="New Request" subtitle="Raise a purchase" onClick={() => navigate("/new-request")} primary />
            )}
            {canViewQueue && (
              <QuickActionCard testid="qa-approvals" icon={Inbox} label="Approval Feed" subtitle={`${data.pending_count || 0} pending${!canApproveRequests ? " · view only" : ""}`} onClick={() => navigate("/approvals")} />
            )}
            {canViewQueue && (
              <QuickActionCard testid="qa-queue" icon={CreditCard} label="Payment Queue" subtitle={`${data.payments_due_count || 0} due`} onClick={() => navigate("/queue")} />
            )}
            {canViewReports && (
              <QuickActionCard testid="qa-reports" icon={BarChart3} label="Reports" subtitle="EOD · EOW · EOM" onClick={() => navigate("/reports")} />
            )}
            {canCreateRequest && (
              <QuickActionCard testid="qa-mine" icon={Receipt} label="My Requests" subtitle="Track yours" onClick={() => navigate("/my-requests")} />
            )}
            {isAdmin && (
              <QuickActionCard testid="qa-admin" icon={Store} label="Admin Panel" subtitle="Users · Vendors" onClick={() => navigate("/admin")} />
            )}
          </div>
        </section>

      </div>

      {/* Site drilldown modal */}
      <Dialog open={!!drilldown} onOpenChange={(o) => { if (!o) setDrilldown(null); }}>
        <DialogContent className="max-w-2xl rounded-2xl border-0 shadow-2xl p-0 gap-0 max-h-[85vh] overflow-y-auto">
          <DialogHeader className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-white z-10 rounded-t-2xl">
            <DialogTitle className="font-display font-bold tracking-tight text-slate-900" data-testid="drilldown-title">
              {drilldown?.site}
              <span className="ml-3 font-mono text-[11px] uppercase tracking-widest text-olive font-bold">
                {SITE_PRESETS.find((p) => p.key === chartPreset)?.label || "—"}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-5">
            {drilldownLoading && <div className="font-mono text-xs text-slate-500">Loading…</div>}
            {drilldown?.data && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <DrillCell label="Total Spend" value={formatINR(drilldown.data.total_spend)} accent="olive" />
                  <DrillCell label="Requests" value={drilldown.data.total_requests} />
                  <DrillCell label="Approved" value={drilldown.data.approved_count} />
                  <DrillCell label="Paid" value={drilldown.data.paid_count} accent="olive" />
                </div>
                {drilldown.data.categories?.length > 0 && (
                  <DrillList title="By Category" items={drilldown.data.categories} k="category" />
                )}
                {drilldown.data.payment_modes?.length > 0 && (
                  <DrillList title="By Payment Mode" items={drilldown.data.payment_modes} k="mode" />
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

/* ──────────────── Page Shell: sidebar + mobile header + bottom nav ──────────────── */
function PageShell({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          {/* Mobile top bar */}
          <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-xl tracking-tighter text-olive">VOW</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Control</span>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <EmailSettingsButton />
              {user && (
                <button data-testid="logout-btn" onClick={logout} className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-olive">Logout</button>
              )}
            </div>
          </header>
          {children}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}

/* Mobile bottom nav — preserves the existing bottom nav UX */
function MobileBottomNav() {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role;
  const perms = user.permissions_effective || {};
  const isAdmin = role === "admin";
  const items = [{ to: "/dashboard", icon: BarChart3, label: "Home" }];
  if (isAdmin || perms.create_requests)    items.push({ to: "/new-request", icon: Plus, label: "New", primary: !isAdmin });
  if (isAdmin || perms.view_payment_queue) items.push({ to: "/approvals", icon: Inbox, label: "Approve", primary: !!perms.approve_requests && !isAdmin });
  if (isAdmin || perms.view_payment_queue) items.push({ to: "/queue", icon: CreditCard, label: "Queue", primary: role === "accounts" });
  if (isAdmin)                             items.push({ to: "/admin", icon: Store, label: "Admin" });
  if (!isAdmin && (perms.view_reports))    items.push({ to: "/reports", icon: BarChart3, label: "Reports" });

  return (
    <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-stretch z-50">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <button
            key={it.to + it.label}
            onClick={() => navigate(it.to)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 ${it.primary ? "bg-olive text-white" : "text-slate-700"}`}
          >
            <Icon size={18} strokeWidth={1.8}/>
            <span className="font-mono text-[9px] uppercase tracking-widest font-bold">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ──────────────── Summary card with sparkline ──────────────── */
function SummaryCard({ testid, title, primaryValue, primaryLabel, secondaryValue, secondaryLabel, accent, delta, spark, onClick }) {
  const uid = React.useId();
  const gradId = `sp-${accent || "slate"}-${uid.replace(/:/g, "")}`;
  const palette = {
    olive: { stroke: "#6B7F3F", color: "#6B7F3F", soft: "bg-olive/5", text: "text-olive" },
    amber: { stroke: "#D97706", color: "#D97706", soft: "bg-amber-50", text: "text-amber-600" },
    red:   { stroke: "#DC2626", color: "#DC2626", soft: "bg-red-50",   text: "text-red-600" },
  }[accent] || { stroke: "#475569", color: "#475569", soft: "bg-slate-50", text: "text-slate-700" };

  const sparkData = (spark || []).map((v, i) => ({ i, v: Number(v) || 0 }));
  const hasSpark = sparkData.some((p) => p.v > 0);

  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`group relative text-left bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/70 p-5 lg:p-6 overflow-hidden transition ${onClick ? "hover:shadow-lg hover:-translate-y-0.5" : ""}`}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="font-display font-bold text-slate-700 text-sm lg:text-base">{title}</h3>
        {delta != null && (
          <span className={`inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest ${delta >= 0 ? "text-olive" : "text-red-600"}`}>
            {delta >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>} {Math.abs(delta)}%
          </span>
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-display font-black tracking-tight text-3xl lg:text-4xl xl:text-[40px] leading-none ${palette.text}`}>
            {primaryValue}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">{primaryLabel}</div>
        </div>
        <div className="w-[40%] h-14 lg:h-16 -mr-1">
          {hasSpark ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={palette.color} stopOpacity={0.35}/>
                    <stop offset="100%" stopColor={palette.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={palette.stroke} strokeWidth={2} fill={`url(#${gradId})`} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className={`w-full h-full rounded-lg ${palette.soft}`} aria-hidden="true" />
          )}
        </div>
      </div>

      <div className="mt-5 pt-3 border-t border-slate-100 flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{secondaryLabel}</span>
        <span className="font-display font-bold text-slate-800 text-sm lg:text-base">{secondaryValue}</span>
      </div>
    </button>
  );
}

/* ──────────────── Site Spend bar chart ──────────────── */
function SiteSpendChart({ sites, onSelect }) {
  const rows = useMemo(
    () => (sites || []).map((s, i) => ({ name: s.site, value: Math.round(s.amount || 0), idx: i })),
    [sites]
  );
  if (rows.length === 0) {
    return (
      <div className="h-[280px] lg:h-[340px] flex items-center justify-center text-sm text-slate-400">
        No site spend yet
      </div>
    );
  }
  const INTER_STACK = "Inter, ui-sans-serif, system-ui, sans-serif";
  return (
    <div className="w-full h-[280px] lg:h-[340px]" data-testid="site-spend-bars">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 12, right: 8, left: 0, bottom: 32 }} barCategoryGap="28%">
          <defs>
            <linearGradient id="bar-olive" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6B7F3F" stopOpacity={0.95}/>
              <stop offset="100%" stopColor="#6B7F3F" stopOpacity={0.75}/>
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fill: "#64748B", fontSize: 12, fontFamily: INTER_STACK, fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            interval={0}
            angle={-12}
            textAnchor="end"
            height={52}
            tickMargin={6}
          />
          <YAxis
            tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: INTER_STACK, fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v >= 1000 ? `₹${Math.round(v/1000)}k` : `₹${v}`}
            width={56}
          />
          <Tooltip
            cursor={{ fill: "rgba(107,127,63,0.06)" }}
            contentStyle={{
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
              fontFamily: INTER_STACK,
              fontSize: 13,
              padding: "10px 12px",
            }}
            itemStyle={{ color: "#0F172A", fontWeight: 600 }}
            labelStyle={{ fontWeight: 600, color: "#475569", marginBottom: 4 }}
            formatter={(value) => [`₹${value.toLocaleString("en-IN")}`, "Spend"]}
          />
          <Bar
            dataKey="value"
            radius={[10, 10, 0, 0]}
            cursor="pointer"
            onClick={(p) => p?.payload?.name && onSelect(p.payload.name)}
          >
            {rows.map((_row, i) => <Cell key={`b-${i}`} fill="url(#bar-olive)" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ──────────────── Recent Activity row (Master Admin only — restored v2.13.1) ──────────────── */
function ActivityRow({ r, onClick }) {
  const status = r.status || "";
  const isPaid = status === "paid" || status === "recorded";
  const isEmergency = r.urgency === "emergency" || r.is_emergency;
  const paidMode = r.actual_payment_mode || r.payment_mode;
  const pill = isPaid
    ? { cls: "bg-olive/10 text-olive", text: paidMode ? `Paid · ${paidMode}` : "Paid" }
    : status === "approved" ? { cls: "bg-amber-100 text-amber-700", text: "Pending Payment" }
    : status === "rejected" ? { cls: "bg-slate-100 text-slate-600", text: "Rejected" }
    : { cls: "bg-slate-100 text-slate-600", text: status || "—" };

  const actor = r.created_by_name || r.created_by_email?.split("@")[0] || "Someone";
  const initials = (actor || r.vendor_name || "?").split(/\s|\./).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <button
      data-testid={`recent-activity-${r.request_id}`}
      onClick={onClick}
      className="w-full text-left flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-xs shrink-0 ${
        isEmergency ? "bg-red-50 text-red-600" : isPaid ? "bg-olive/10 text-olive" : "bg-amber-50 text-amber-700"
      }`}>
        {isEmergency ? <Flame size={14}/> : initials || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm text-slate-900 truncate">
          {actor} raised {formatINR(r.approved_amount || r.amount)}
        </div>
        <div className="font-mono text-[11px] text-slate-500 truncate">
          {(r.vendor_name && r.vendor_name !== "NA" ? r.vendor_name : "Unknown vendor")} · {r.site}
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {r.approved_by_name && <span className="font-mono text-[10px] text-slate-500">Approved by {r.approved_by_name}</span>}
          <span className={`inline-flex font-mono text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
            {pill.text}
          </span>
        </div>
      </div>
    </button>
  );
}


/* ──────────────── Requestor-only Dashboard (v2.13) ────────────────
   Requestors see ONLY: their own request counts by status and a
   shortcut to raise a new one. Everything else (site totals, spend,
   activity feed, others' requests) is intentionally absent. */
function RequestorDashboard({ user, data, loading, navigate }) {
  const mr = data?.my_requests || { total: 0, pending: 0, approved: 0, paid: 0, rejected: 0, failed: 0 };
  const firstName = (user?.name || user?.email || "").split(/\s|@/)[0] || "there";
  const buckets = [
    { key: "pending",  label: "Pending",  value: mr.pending,  accent: "amber",  filter: "pending" },
    { key: "approved", label: "Approved", value: mr.approved, accent: "olive",  filter: "approved" },
    { key: "paid",     label: "Paid",     value: mr.paid,     accent: "emerald",filter: "paid" },
    { key: "rejected", label: "Rejected", value: mr.rejected, accent: "slate",  filter: "rejected" },
  ];
  const accentClass = (a) => ({
    amber:   "text-amber-700",
    olive:   "text-olive",
    emerald: "text-emerald-600",
    slate:   "text-slate-500",
  }[a] || "text-slate-900");

  return (
    <div className="px-5 sm:px-8 lg:px-10 xl:px-14 py-6 lg:py-10 max-w-[1200px] mx-auto pb-32 lg:pb-10">
      <header className="mb-8">
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-[44px] leading-none tracking-tight text-slate-900">
          Welcome back, <span className="text-olive">{firstName}</span>
        </h1>
        <p className="mt-3 font-mono text-sm text-slate-500">Your requests, in one place.</p>
      </header>

      <section
        data-testid="requestor-summary"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8"
      >
        {buckets.map((b) => (
          <button
            key={b.key}
            data-testid={`requestor-bucket-${b.key}`}
            onClick={() => navigate(`/my-requests?status=${b.filter}`)}
            className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/80 p-5 lg:p-6 text-left hover:ring-olive/40 hover:shadow-md transition"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              {b.label}
            </div>
            <div className={`font-display font-black text-4xl lg:text-5xl tracking-tight ${accentClass(b.accent)}`}>
              {loading ? "—" : b.value}
            </div>
          </button>
        ))}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <button
          data-testid="qa-new"
          onClick={() => navigate("/new-request")}
          className="rounded-2xl p-6 lg:p-7 text-left bg-olive text-white shadow-sm ring-1 ring-olive hover:bg-olive-700 transition"
        >
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-3.5">
            <Plus size={20} strokeWidth={2}/>
          </div>
          <div className="font-display font-bold text-lg">New Request</div>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-white/80">
            Raise a purchase
          </div>
        </button>
        <button
          data-testid="qa-mine"
          onClick={() => navigate("/my-requests")}
          className="rounded-2xl p-6 lg:p-7 text-left bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/70 hover:ring-olive/40 hover:shadow-md transition"
        >
          <div className="w-11 h-11 rounded-xl bg-olive/10 text-olive flex items-center justify-center mb-3.5">
            <Receipt size={20} strokeWidth={2}/>
          </div>
          <div className="font-display font-bold text-lg">My Requests</div>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            {mr.total || 0} total · track status
          </div>
        </button>
      </section>
    </div>
  );
}


/* ──────────────── Quick Action card ──────────────── */
function QuickActionCard({ icon: Icon, label, subtitle, onClick, testid, primary }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`group rounded-2xl p-5 lg:p-6 text-left transition shadow-sm ring-1 ${
        primary
          ? "bg-olive text-white ring-olive hover:bg-olive-700"
          : "bg-white text-slate-900 ring-slate-200/70 hover:ring-olive/40 hover:shadow-md"
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3.5 ${primary ? "bg-white/15" : "bg-olive/10 text-olive group-hover:bg-olive group-hover:text-white transition"}`}>
        <Icon size={18} strokeWidth={2}/>
      </div>
      <div className="font-display font-bold text-base">{label}</div>
      <div className={`mt-1 font-mono text-[11px] uppercase tracking-widest ${primary ? "text-white/80" : "text-slate-500"}`}>{subtitle}</div>
    </button>
  );
}

/* ──────────────── Pill controls (filters) ──────────────── */
function PillDropdown({ value, onChange, options, placeholder, testid }) {
  return (
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none rounded-full bg-white ring-1 ring-slate-200 hover:ring-olive/60 focus:ring-2 focus:ring-olive focus:outline-none pl-4 pr-9 py-2 font-mono text-xs font-bold cursor-pointer transition shadow-sm"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label || placeholder}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
    </div>
  );
}

function PillButton({ icon: Icon, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="rounded-full bg-white ring-1 ring-slate-200 hover:ring-olive/60 px-4 py-2 font-mono text-xs font-bold inline-flex items-center gap-2 cursor-pointer transition shadow-sm"
    >
      {Icon && <Icon size={13} className="text-olive"/>}
      <span>{children}</span>
      <ChevronDown size={13} className="text-slate-400"/>
    </button>
  );
}

function LabeledInput({ label, testid, type, value, onChange }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-olive focus:outline-none px-4 py-3 font-mono text-sm transition"
      />
    </div>
  );
}

/* ──────────────── Drilldown helpers ──────────────── */
const DrillCell = ({ label, value, accent }) => (
  <div className="rounded-xl bg-slate-50 p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-display font-black text-lg lg:text-xl mt-1 ${accent === "olive" ? "text-olive" : "text-slate-900"}`}>{value}</div>
  </div>
);
const DrillList = ({ title, items, k }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <div className="rounded-xl ring-1 ring-slate-200/70 overflow-hidden bg-white">
      {items.map((row) => (
        <div key={row[k]} className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 last:border-b-0">
          <div>
            <div className="font-display text-sm font-semibold text-slate-900">{row[k]}</div>
            <div className="font-mono text-[10px] text-slate-500">{row.count} payment{row.count === 1 ? "" : "s"}</div>
          </div>
          <div className="font-display font-black text-sm">{formatINR(row.amount)}</div>
        </div>
      ))}
    </div>
  </div>
);
